import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import type { NotificationDto } from '@/api/platform';
import { useMe, useSessionStatus } from '@/auth/useAuth';
import { keysForNotificationTypes } from '@/lib/notificationRefresh';
import { openNotificationStream } from '@/lib/notificationStream';
import { qk } from '@/lib/queryKeys';
import { titleBadge } from '@/lib/titleBadge';
import { desktopEnabled, showDesktop, takeNew, TOAST_MS } from './notificationAlerts';

/** Hidden this long (and no desktop notifications wanted) → close the stream; polling also stops in the background. */
const HIDDEN_GRACE_MS = 60_000;

export interface RealtimeOptions {
  items: readonly NotificationDto[] | undefined;
  unread: number;
  /** Mark read + navigate (the bell's own click behaviour). */
  open: (n: NotificationDto) => void;
  /** Re-read when the user toggles desktop notifications. */
  desktopVersion: number;
}

/**
 * Realtime layer of the notification bell:
 *  - SSE stream → refetch the list (debounced) as soon as a notification is created for the user;
 *  - new items → corner toast (click = open + mark read), refresh of the screens they concern, optional OS notification when hidden;
 *  - unread count in the tab title.
 * Returns whether the stream is connected (the bell then polls slower).
 */
export function useNotificationsRealtime({ items, unread, open, desktopVersion }: RealtimeOptions): boolean {
  const qc = useQueryClient();
  const status = useSessionStatus();
  const userId = useMe().data?.id;
  const [connected, setConnected] = useState(false);
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
  const watermark = useRef<number | null>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const desktop = desktopEnabled(userId) && desktopVersion >= 0;

  // Visibility with a grace period, so quick tab switches don't reconnect.
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | undefined;
    const onChange = () => {
      clearTimeout(t);
      if (document.visibilityState === 'hidden') t = setTimeout(() => setVisible(false), HIDDEN_GRACE_MS);
      else setVisible(true);
    };
    document.addEventListener('visibilitychange', onChange);
    return () => {
      clearTimeout(t);
      document.removeEventListener('visibilitychange', onChange);
    };
  }, []);

  const refetchSoon = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const refetchList = useCallback(() => {
    clearTimeout(refetchSoon.current);
    refetchSoon.current = setTimeout(() => void qc.invalidateQueries({ queryKey: qk.notifications }), 250);
  }, [qc]);

  // Stream: only while signed in, and while visible unless OS notifications are on. Logout / rejected refresh closes it.
  const active = status === 'authenticated' && (visible || desktop);
  useEffect(() => {
    if (!active) return;
    let wasOpen = false;
    const s = openNotificationStream({
      onEvent: (e) => {
        // "ready" after a reconnect = we may have missed signals: catch up once.
        if (e.type === 'notification' || e.type === 'resync' || (e.type === 'ready' && wasOpen)) refetchList();
        if (e.type === 'ready') wasOpen = true;
      },
      onStatus: (st) => setConnected(st === 'open'),
    });
    return () => {
      s.stop();
      setConnected(false);
      clearTimeout(refetchSoon.current);
    };
  }, [active, refetchList]);

  // A different user (or signed out): start over so their old notifications never pop up.
  useEffect(() => {
    watermark.current = null;
  }, [userId, status]);

  // New notifications → toasts + refresh the screens they concern + OS notification when the tab is hidden.
  useEffect(() => {
    if (!items) return;
    const { fresh, watermark: next } = takeNew(items, watermark.current);
    watermark.current = next;
    if (fresh.length === 0) return;
    for (const key of keysForNotificationTypes(fresh.map((n) => n.type))) void qc.invalidateQueries({ queryKey: key });
    const hidden = document.visibilityState === 'hidden';
    for (const n of fresh) {
      if (hidden && desktop) showDesktop(n, () => openRef.current(n));
      toast.custom((id) => <NotificationToast n={n} onOpen={() => { toast.dismiss(id); openRef.current(n); }} />, {
        id: `notification-${n.id}`,
        duration: TOAST_MS,
      });
    }
  }, [items, qc, desktop]);

  useEffect(() => {
    titleBadge.setCount(status === 'authenticated' ? unread : 0);
  }, [unread, status]);
  useEffect(() => () => titleBadge.setCount(0), []);

  return connected;
}

export function NotificationToast({ n, onOpen }: { n: NotificationDto; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-[min(356px,calc(100vw-32px))] gap-2.5 rounded-lg border border-line bg-surface p-3 text-left font-sans shadow-lg transition-colors hover:bg-surface-2"
    >
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent" aria-hidden>
        <Bell className="size-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-ink">{n.title}</span>
        {n.body && <span className="mt-0.5 line-clamp-2 block text-xs text-muted">{n.body}</span>}
        {n.link && <span className="mt-1 block text-2xs font-medium text-accent">Xem chi tiết</span>}
      </span>
    </button>
  );
}
